/**
 * "メディア計測" debug panel.
 *
 * Puts the two numbers that explain the transmitted-audio dropouts side by side:
 *
 *   - how much the MAIN THREAD is blocked  (mainThreadMonitor)
 *   - how much of the SENT AUDIO is missing (NkfAecEffect's rolling window)
 *
 * They belong together because one causes the other: the cleaned audio crosses
 * the main thread twice on its way out and the emit worklet buffers only ~96 ms,
 * so a longer stall costs twice — silence while frozen, then dropped chunks when
 * the backlog floods the queue on release. That is why 空 and 溢 come out equal
 * when the thread is the problem, and why pressure and loss should track each
 * other here.
 *
 * Plain DOM on purpose: same approach as the existing waveform overlay and log
 * panel, so it works even when React is busy — which is exactly when we need to
 * read it.
 */
// @ts-ignore - plain JS effect module without type declarations.
import { getNkfMediaStats } from '../stream-effects/nkf-aec/NkfAecEffect.js';
import { getMainThreadStats, startMainThreadMonitor, stopMainThreadMonitor } from './mainThreadMonitor';
/** Redraw cadence. Fast enough to feel live, slow enough not to add pressure. */
const REFRESH_MS = 500;
let _el = null;
let _timer = null;
let _visible = false;
/**
 * Colours a figure by severity so the panel can be read at a glance.
 *
 * @param {number} v - The value.
 * @param {number} warn - Value at which it becomes a concern.
 * @param {number} bad - Value at which it is clearly a problem.
 * @returns {string}
 */
function _sev(v, warn, bad) {
    if (v >= bad) {
        return '#ff6b6b';
    }
    if (v >= warn) {
        return '#f5a524';
    }
    return '#7ee787';
}
/**
 * One "label — value" line.
 *
 * @param {string} label - Left column.
 * @param {string} value - Right column.
 * @param {string} [colour] - Value colour.
 * @returns {string}
 */
function _row(label, value, colour) {
    return '<div style="display:flex;justify-content:space-between;gap:12px;padding:1px 0">'
        + `<span style="opacity:.72">${label}</span>`
        + `<b style="font-variant-numeric:tabular-nums${colour ? `;color:${colour}` : ''}">${value}</b>`
        + '</div>';
}
/**
 * Section heading.
 *
 * @param {string} title - The heading.
 * @returns {string}
 */
function _head(title) {
    return `<div style="margin:8px 0 3px;font-weight:700;opacity:.9">${title}</div>`;
}
/**
 * Redraws the panel from the current measurements.
 *
 * @returns {void}
 */
function _render() {
    if (!_el) {
        return;
    }
    const mt = getMainThreadStats();
    const md = getNkfMediaStats();
    const last = md.last;
    const parts = [];
    parts.push('<div style="font-weight:700;margin-bottom:4px">📊 メディア計測'
        + '<span style="opacity:.5;font-weight:400"> — 直近5秒</span></div>');
    parts.push(_head('メインスレッド'));
    parts.push(_row('圧迫率', `${mt.pressurePct.toFixed(1)} %`, _sev(mt.pressurePct, 10, 25)));
    if (mt.exact) {
        parts.push(_row('長タスク', `${mt.longTasks}回 / 最長 ${Math.round(mt.worstMs)}ms`, _sev(mt.worstMs, 96, 200)));
    }
    else {
        parts.push(_row('長タスク', '非対応（遅延のみで推定）', '#f5a524'));
    }
    parts.push(_row('遅延(drift)', `平均 ${Math.round(mt.driftAvgMs)}ms / 最大 ${Math.round(mt.driftMaxMs)}ms`, _sev(mt.driftMaxMs, 96, 200)));
    parts.push(_head('送信音声の欠落'));
    parts.push(_row('欠落率', `${md.lossPct.toFixed(1)} %`, _sev(md.lossPct, 1, 5)));
    parts.push(_row('空 (枯渇)', `${Math.round(md.urMs)} ms`));
    parts.push(_row('溢 (破棄)', `${Math.round(md.trimMs)} ms`));
    parts.push(_row('回数', `${md.events}回 (${md.eventsPerSec.toFixed(1)}/秒)`));
    // Where the audio is on its way out, and how much room is left before any
    // of it is lost. The dropout figures above only say it already happened;
    // these say how close the next hiccup is to doing it again.
    parts.push(_head('送信経路'));
    if (last && last.qAvg != null) {
        // Depth is what buys tolerance: at 0-1 any stall punches a hole, at
        // 2-3 there is room to absorb one.
        parts.push(_row('キュー残', `最小 ${last.qMin} / 平均 ${last.qAvg.toFixed(1)} / 上限 ${last.qCap}`, last.qMin <= 0 ? '#ff6b6b' : last.qAvg < 1.5 ? '#f5a524' : '#7ee787'));
        parts.push(_row('　', `≒ ${Math.round(last.qAvg * 32)} ms の余裕`));
    }
    else if (last) {
        parts.push(_row('キュー残', '—'));
    }
    if (last && last.pendingMax != null) {
        // Reaching the cap is what turns a chunk into raw mic (bypass).
        parts.push(_row('未処理', `最大 ${last.pendingMax} / 上限 ${last.pendingCap}`, _sev(last.pendingMax, last.pendingCap - 1, last.pendingCap)));
    }
    if (last && last.onnxAvg != null) {
        // One chunk is 32 ms of audio; inference slower than that cannot keep up.
        parts.push(_row('推論', `平均 ${last.onnxAvg.toFixed(1)} / 最大 ${last.onnxMax.toFixed(1)} ms`, _sev(last.onnxAvg, 20, 32)));
    }
    if (last && last.rttAvg != null) {
        // Round trip minus inference is time spent waiting on the main thread.
        const sched = last.onnxAvg != null ? last.rttAvg - last.onnxAvg : null;
        parts.push(_row('往復', `平均 ${last.rttAvg.toFixed(1)} / 最大 ${last.rttMax.toFixed(1)} ms`, _sev(last.rttAvg, 64, 96)));
        if (sched != null) {
            parts.push(_row('うち待ち時間', `${sched.toFixed(1)} ms`, _sev(sched, 20, 50)));
        }
    }
    parts.push(_head('AEC'));
    if (last) {
        parts.push(_row('処理落ち', `${(last.bypassRate * 100).toFixed(1)} % (${last.bypassed}/${last.chunks})`, _sev(last.bypassRate * 100, 5, 8)));
        parts.push(_row('ERLE', last.erleDb == null ? '—' : `${last.erleDb.toFixed(1)} dB`, last.erleDb == null ? undefined : (last.erleDb >= 10 ? '#7ee787' : '#f5a524')));
        parts.push(_row('遅延', `${last.farDelaySamples} samples `
            + `(${(last.farDelaySamples * 1000 / 16000).toFixed(1)} ms)`));
        // 31.25/s is the nominal rate (512 samples @ 16 kHz = 32 ms per chunk).
        parts.push(_row('チャンク', `${last.chunksPerSec.toFixed(1)} /秒`, Math.abs(last.chunksPerSec - 31.25) > 3 ? '#f5a524' : undefined));
        parts.push(_row('実行', last.worker ? 'worker' : 'main', last.worker ? undefined : '#f5a524'));
    }
    else {
        parts.push('<div style="opacity:.6;padding:2px 0">まだ集計がありません（AEC 起動待ち）</div>');
    }
    // 空 and 溢 in the same ballpark is the fingerprint of a stalling main
    // thread: the queue empties, then the backlog floods it. Say so rather than
    // leaving the reader to spot the coincidence.
    // Say it before it happens, not only after. A queue that never gets above
    // one chunk is one stall away from a hole even while the loss figures read
    // zero — which is exactly the state that looks fine and is not.
    if (last && last.qAvg != null && last.qAvg < 1.5 && md.lossPct < 1) {
        parts.push('<div style="margin-top:8px;padding:5px 7px;border-radius:6px;'
            + 'background:rgba(245,165,36,.14);border:1px solid rgba(245,165,36,.4);'
            + 'color:#ffcf7a;line-height:1.45">'
            + 'まだ途切れていないが、キューに余裕がない — 次の詰まりで穴が空く</div>');
    }
    if (md.urMs > 50 && md.trimMs > 50) {
        const ratio = md.urMs / md.trimMs;
        if (ratio > 0.6 && ratio < 1.7) {
            parts.push('<div style="margin-top:8px;padding:5px 7px;border-radius:6px;'
                + 'background:rgba(245,165,36,.14);border:1px solid rgba(245,165,36,.4);'
                + 'color:#ffcf7a;line-height:1.45">'
                + '空と溢がほぼ同量 — メインスレッド停止の典型パターン</div>');
        }
    }
    _el.innerHTML = parts.join('');
}
/**
 * Shows or hides the panel. Measurement runs only while it is open.
 *
 * @param {boolean} visible - True to show.
 * @returns {void}
 */
export function setNkfMediaStatsVisible(visible) {
    if (typeof document === 'undefined') {
        return;
    }
    _visible = Boolean(visible);
    if (!_el) {
        const el = document.createElement('div');
        el.id = 'nkf-media-stats';
        // Top-left, below the waveform overlay's slot, above the log panel at
        // the bottom — so all three can be open without overlapping.
        el.style.cssText =
            'position:fixed;left:12px;top:54px;z-index:99999;width:300px;'
                + 'padding:10px 12px;background:rgba(16,18,24,.88);'
                + 'backdrop-filter:blur(10px);-webkit-backdrop-filter:blur(10px);'
                + 'border:1px solid rgba(255,255,255,.08);border-radius:12px;'
                + 'box-shadow:0 8px 28px rgba(0,0,0,.45);color:#e6e9ef;'
                + 'font:11px/1.45 ui-monospace,monospace;pointer-events:none;'
                + 'user-select:none;display:none;';
        document.body.appendChild(el);
        _el = el;
    }
    _el.style.display = _visible ? 'block' : 'none';
    if (_visible) {
        startMainThreadMonitor();
        _render();
        if (!_timer) {
            _timer = setInterval(_render, REFRESH_MS);
        }
    }
    else {
        if (_timer) {
            clearInterval(_timer);
            _timer = null;
        }
        stopMainThreadMonitor();
    }
}
/**
 * Whether the panel is currently shown.
 *
 * @returns {boolean}
 */
export function isNkfMediaStatsVisible() {
    return _visible;
}
